import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import './notifications.scss';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Label, Tab, MenuItem } from 'semantic-ui-react';
import getCurrentOrg from 'helpers/getCurrentOrg';
import Toolbar from 'components/common/Toolbar';
import ToolbarFooter from 'components/common/Toolbar/ToolbarFooter';
import getMicroLoanNotifications from 'redux/actions/loanRequests/getMicroLoanNotifications';
import readAllMicroLoanNotifications from 'redux/actions/loanRequests/readAllMicroLoanNotifications';
import readMicroLoanNotification from 'redux/actions/loanRequests/readMicroLoanNotification';
import NotificationList from './NotificationList';

const panes = ({
  data,
  meta,
  loading,
  onReadNotification,
  onActionSuccess,
}) => [
  {
    menuItem: (
      <MenuItem key="submitted">
        All{' '}
        {meta?.total > 0 && (
          <Label
            style={{
              backgroundColor: '#F9FAFF',
              color: '#323652',
              border: '2px solid #484E730F',
            }}
          >
            {meta.total ?? 0}
          </Label>
        )}
      </MenuItem>
    ),
    render: () => (
      <NotificationList
        items={data}
        loading={loading}
        onReadNotification={onReadNotification}
        onActionSuccess={onActionSuccess}
      />
    ),
  },
  {
    menuItem: (
      <MenuItem key="submitted">
        Unread{' '}
        {meta?.unread > 0 && (
          <Label color="red">{meta.unread ?? 0}</Label>
        )}
      </MenuItem>
    ),
    render: () => (
      <NotificationList
        items={data}
        loading={loading}
        onReadNotification={onReadNotification}
        onActionSuccess={onActionSuccess}
      />
    ),
  },
];

const Notifications = ({ location, history, match }) => {
  const { push } = useHistory();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get('type');

  const { data, meta, loading } = useSelector(
    ({ loanRequests: { microLoanNotifications } }) =>
      microLoanNotifications,
  );

  const { loading: readAllLoading } = useSelector(
    ({ loanRequests: { readAllMicroLoanNotifications } }) =>
      readAllMicroLoanNotifications,
  );

  const statusQuery = type === 'unread' ? { status: 'unread' } : {};

  useEffect(() => {
    getMicroLoanNotifications(statusQuery)(dispatch);
  }, [type]);

  const currentUserOrganization = getCurrentOrg();

  const { save_type } = currentUserOrganization || {};

  const tabs = {
    all: { index: 0, name: 'all' },
    unread: { index: 1, name: 'unread' },
  };

  const activeIndex = !tabs[type] ? 0 : tabs[type].index;

  const handleTabChange = (e, { activeIndex }) => {
    const key = Object.keys(tabs).find(
      key => tabs[key].index === activeIndex,
    );
    push(`/notifications?type=${key}`);
  };

  const onSearch = queries => {
    getMicroLoanNotifications({ ...statusQuery, ...queries })(
      dispatch,
    );
  };

  const onPaginate = queries => {
    getMicroLoanNotifications({ ...statusQuery, ...queries })(
      dispatch,
    );
  };

  const onMarkAllAsRead = () => {
    readAllMicroLoanNotifications(statusQuery)(dispatch);
  };

  const onReadNotification = requestId => {
    readMicroLoanNotification(requestId, statusQuery)(dispatch);
  };

  const onActionSuccess = () => {
    getMicroLoanNotifications(statusQuery)(dispatch);
  };

  return (
    <div className="notifications">
      <Toolbar
        header={{
          title: 'Notifications',
          titleComponent: (
            <div className="flex flex-row flex-center-vertical">
              <h2 className="notifications-header">Notifications</h2>
            </div>
          ),
          navigation: false,
          rightComponent: (
            <>
              <Button
                primary
                loading={readAllLoading}
                disabled={readAllLoading}
                onClick={onMarkAllAsRead}
                content="Mark all as read"
                style={{ marginRight: '10px' }}
              />
              <ToolbarFooter
                onSearch={onSearch}
                onPaginate={onPaginate}
                paginationMeta={meta}
              />
            </>
          ),
        }}
      />

      <div className="notifications-tab-items">
        <Tab
          activeIndex={activeIndex}
          menu={{ secondary: true, pointing: true }}
          panes={panes({
            data,
            meta,
            loading,
            location,
            history,
            match,
            save_type,
            onReadNotification,
            onActionSuccess,
          })}
          onTabChange={handleTabChange}
        />
      </div>
    </div>
  );
};

Notifications.propTypes = {
  location: PropTypes.objectOf(Object).isRequired,
  history: PropTypes.objectOf(Object).isRequired,
  match: PropTypes.shape({
    params: PropTypes.oneOfType([PropTypes.object]),
  }).isRequired,
};

export default Notifications;
